import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';

interface ResolveNavigateOptions {
	path: string;
	fadeIn?: boolean;
	fadeOut?: boolean;
}

export function useResolveNavigate() {
	const navigate = useNavigate();
	const location = useLocation();

	const resolveNavigate = useCallback(
		({ path, fadeIn = true, fadeOut = true }: ResolveNavigateOptions) => {
			// If we're already on the target path, do nothing (noop)
			if (location.pathname === path) {
				return;
			}
			if (!fadeOut && !fadeIn) {
				// No transition effects, navigate immediately
				navigate(path);
				return;
			}

			if (!fadeOut) {
				// Skip fade out, navigate immediately (page will fade in automatically)
				navigate(path);
				return;
			}

			if (!fadeIn) {
				// Fade out then navigate without fade in
				// Find the page transition wrapper and trigger fade out
				const pageWrapper = document.querySelector('[data-page-transition]');
				if (pageWrapper) {
					pageWrapper.classList.remove('opacity-100');
					pageWrapper.classList.add('opacity-0');
					
					// Navigate after fade out completes
					setTimeout(() => {
						navigate(path);
					}, 250); // Match the fade out duration
				} else {
					// Fallback if wrapper not found
					navigate(path);
				}
				return;
			}

			// Default: fade out, navigate, then fade in (handled by PageTransition component)
			const pageWrapper = document.querySelector('[data-page-transition]');
			if (pageWrapper) {
				pageWrapper.classList.remove('opacity-100');
				pageWrapper.classList.add('opacity-0');
				
				// Navigate after fade out completes
				setTimeout(() => {
					navigate(path);
				}, 250); // Match the fade out duration
			} else {
				// Fallback if wrapper not found
				navigate(path);
			}
		},
		[navigate, location.pathname]
	);

	return resolveNavigate;
}